import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import ArrowRightIcon from '../../../static/img/arrowright.svg';
import GreenTickIcon from '../../../static/img/GreenTick.svg';
import {
  formatQuestionLabel,
  parseHtmlInert,
  parseQuestionHtml,
  sortBySerialNumber,
} from '../../SubmissionView/submissionViewUtils';

const PURPLE = '#7200e0';
const ACTIVE_ROW_BG = '#FBF7FE';
const TREE_LINE = '#c9c6cc';
const BADGE_GREY = '#f2f1f3';
const MAX_SNIPPET_LENGTH = 52;

const getParentSerial = (question) =>
  question.parentQuestionSerialNumber ?? question.parentSerialNumber;

const groupQuestions = (questions) => {
  const groups = {};
  const topLevel = [];

  questions.forEach((q) => {
    const parent = getParentSerial(q);
    if (parent != null && parent !== '') {
      const key = String(parent);
      (groups[key] = groups[key] || []).push(q);
    } else {
      topLevel.push(q);
    }
  });

  return sortBySerialNumber(topLevel).map((parentQ) => ({
    parent: parentQ,
    children: sortBySerialNumber(groups[String(parentQ.serialNumber)] || []),
  }));
};

const extractText = (htmlQuestion) => {
  if (!htmlQuestion || typeof document === 'undefined') return '';
  const tmp = parseHtmlInert(htmlQuestion);
  return (tmp.textContent || tmp.innerText || '').trim();
};

const needsTruncation = (text) => text.length > MAX_SNIPPET_LENGTH;

const getQuestionListText = (question, hasChildren = false) => {
  if (!question) return '';
  const parsed = parseQuestionHtml(question.question, hasChildren, question);

  if (hasChildren) {
    return parsed.title || extractText(question.question);
  }

  if (parsed.hasStructuredTitle) {
    const description = extractText(parsed.descriptionHtml);
    return description ? `${parsed.title} ${description}` : parsed.title;
  }

  const title = question.title?.trim();
  const body = extractText(question.question);
  if (title && body) return `${title} ${body}`;
  return title || body;
};

const getQuestionMarks = (question) => {
  if (!question) return null;
  if (question.marksOutOf != null) return question.marksOutOf;
  if (question.marks != null) return question.marks;
  if (question.totalMarks != null) return question.totalMarks;
  return null;
};

const formatMarks = (marks) => `${marks} mark${marks === 1 ? '' : 's'}`;

const TextWithShowMore = ({ text, TextComponent, WrapperComponent }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isTruncated = needsTruncation(text);

  if (!isTruncated) {
    return (
      <WrapperComponent>
        <TextComponent>{text}</TextComponent>
      </WrapperComponent>
    );
  }

  const displayText = isExpanded ? text : `${text.slice(0, MAX_SNIPPET_LENGTH)}...`;

  return (
    <WrapperComponent>
      <TextComponent>{displayText}</TextComponent>
      <ShowMoreButton
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
      >
        {isExpanded ? 'Show less' : 'Show more'}
      </ShowMoreButton>
    </WrapperComponent>
  );
};

const NestedQuestionList = ({
  questions = [],
  currentQuestionIndex = 0,
  submission,
  onQuestionSelect,
  compact = false,
}) => {
  const groupedQuestions = useMemo(() => {
    if (!questions || questions.length === 0) return [];
    return groupQuestions(questions);
  }, [questions]);
  const currentSerial = questions[currentQuestionIndex]?.serialNumber;

  const hasAnswer = (serialNumber) => {
    if (!submission?.answers) return false;
    const answer = submission.answers.find(
      (a) =>
        String(a.serialNumber) === String(serialNumber) ||
        String(a.questionSerialNumber) === String(serialNumber),
    );
    const raw = answer?.answer?.answer ?? answer?.answer ?? '';
    const text =
      typeof raw === 'string' ? raw.replace(/<[^>]*>/g, '').trim() : '';
    // Handwritten responses live in fileUrls, not text — count them too (F4),
    // or a handwritten question shows no "answered" tick.
    const fileUrls = answer?.answer?.fileUrls;
    return text.length > 0 || (Array.isArray(fileUrls) && fileUrls.length > 0);
  };

  const indexOfSerial = (serialNumber) =>
    questions.findIndex((q) => q.serialNumber === serialNumber);

  const handleQuestionClick = (question) => {
    const group = groupedQuestions.find(
      (g) => g.parent.serialNumber === question.serialNumber,
    );
    const target = group?.children.length ? group.children[0] : question;
    onQuestionSelect?.(indexOfSerial(target.serialNumber));
  };

  const renderChildRow = (question, showTreeBranch = true) => {
    const isActive = String(question.serialNumber) === String(currentSerial);
    const marks = getQuestionMarks(question);
    const questionText = getQuestionListText(question);

    return (
      <ChildQuestionRow
        key={question.serialNumber}
        type="button"
        $isActive={isActive}
        $showBranch={showTreeBranch}
        onClick={() => handleQuestionClick(question)}
      >
        <ChildLabel $isActive={isActive}>
          {formatQuestionLabel(question.serialNumber)}
        </ChildLabel>
        {compact ? (
          <ChildText $compact>{questionText}</ChildText>
        ) : (
          <TextWithShowMore
            text={questionText}
            TextComponent={ChildText}
            WrapperComponent={ChildTextWrapper}
          />
        )}
        {marks != null && <Marks>{formatMarks(marks)}</Marks>}
        <StatusIcon
          src={hasAnswer(question.serialNumber) ? GreenTickIcon : ArrowRightIcon}
          alt=""
          aria-hidden
        />
      </ChildQuestionRow>
    );
  };

  const renderGroup = (group) => {
    const { parent } = group;
    const parentText = getQuestionListText(parent, group.children.length > 0);
    const parentMarks = getQuestionMarks(parent);

    if (!group.children.length) {
      return (
        <QuestionGroup key={parent.serialNumber}>
          <SingleQuestionWrap>{renderChildRow(parent, false)}</SingleQuestionWrap>
        </QuestionGroup>
      );
    }
    return (
      <QuestionGroup key={parent.serialNumber}>
        <TreeBlock>
          <ParentQuestionRow
            type="button"
            onClick={() => handleQuestionClick(parent)}
          >
            <ParentLabel>{formatQuestionLabel(parent.serialNumber)}</ParentLabel>
            {compact ? (
              <ParentTitle $compact>{parentText}</ParentTitle>
            ) : (
              <TextWithShowMore
                text={parentText}
                TextComponent={ParentTitle}
                WrapperComponent={ParentTitleWrapper}
              />
            )}
            {parentMarks != null && (
              <Marks>{formatMarks(parentMarks)}</Marks>
            )}
          </ParentQuestionRow>
          <ChildrenList>
            {group.children.map((childQ) => renderChildRow(childQ, true))}
          </ChildrenList>
        </TreeBlock>
      </QuestionGroup>
    );
  };

  if (!questions || questions.length === 0) {
    return <QuestionsList />;
  }

  return <QuestionsList>{groupedQuestions.map(renderGroup)}</QuestionsList>;
};

export default NestedQuestionList;

const QuestionsList = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 4px 0 20px;
`;

const QuestionGroup = styled.div`
  &:not(:last-child) {
    border-bottom: 1px solid #ececec;
  }
`;

const TreeBlock = styled.div`
  padding: 12px 24px 16px;
`;

const ParentQuestionRow = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
  padding-left: 4px;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: opacity 0.15s ease;
  overflow: hidden;

  &:hover {
    opacity: 0.7;
  }
`;

const ParentLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #f1e6fc;
  color: ${PURPLE};
  font-weight: 600;
  font-size: 13px;
  padding: 4px 10px;
  border-radius: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1.3;
  margin-top: 2px;
`;

const ParentTitleWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
  max-width: 100%;
`;

const ParentTitle = styled.span`
  flex: 1;
  min-width: 0;
  font-weight: 700;
  font-size: 15px;
  color: #181718;
  line-height: 1.4;

  ${(props) =>
    props.$compact
      ? css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `
      : css`
          word-wrap: break-word;
          overflow-wrap: break-word;
          word-break: break-word;
          white-space: normal;
          max-width: 100%;
        `}
`;

const ChildrenList = styled.div`
  margin-left: 20px;
  margin-top: 12px;
  padding-left: 32px;
  border-left: 1px solid ${TREE_LINE};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SingleQuestionWrap = styled.div`
  padding: 12px 24px 16px;
`;

const activeRowStyles = css`
  background: ${ACTIVE_ROW_BG};
  border: 1px solid ${PURPLE};
`;

const inactiveRowStyles = css`
  background: transparent;
  border: 1px solid transparent;
`;

const ChildQuestionRow = styled.button`
  position: relative;
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease, border-color 0.15s ease;
  min-width: 0;

  ${(props) => (props.$isActive ? activeRowStyles : inactiveRowStyles)}

  ${(props) =>
    props.$showBranch &&
    css`
      &::before {
        content: '';
        position: absolute;
        left: -32px;
        top: 50%;
        width: 28px;
        height: 1px;
        background: ${TREE_LINE};
        transform: translateY(-50%);
      }
    `}

  &:hover {
    ${activeRowStyles}
  }
`;

const ChildLabel = styled.span`
  font-weight: 600;
  font-size: 13px;
  padding: 4px 10px;
  border-radius: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1.3;

  ${(props) =>
    props.$isActive
      ? css`
          color: #ffffff;
          background: ${PURPLE};
        `
      : css`
          color: #4b464f;
          background: ${BADGE_GREY};
        `}

  ${ChildQuestionRow}:hover & {
    color: #ffffff;
    background: ${PURPLE};
  }
`;

const ChildTextWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
  max-width: 100%;
`;

const ChildText = styled.span`
  flex: 1;
  min-width: 0;
  font-weight: 400;
  font-size: 14px;
  color: #181718;
  line-height: 1.4;

  ${(props) =>
    props.$compact
      ? css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `
      : css`
          word-wrap: break-word;
          overflow-wrap: break-word;
          word-break: break-word;
          white-space: normal;
          max-width: 100%;
          overflow-wrap: anywhere;
        `}
`;

const ShowMoreButton = styled.button`
  background: none;
  border: none;
  color: ${PURPLE};
  font-size: 13px;
  font-weight: 600;
  padding: 0;
  cursor: pointer;
  align-self: flex-start;
  text-decoration: none;
  line-height: 1.4;

  &:hover {
    text-decoration: underline;
  }
`;

const StatusIcon = styled.img`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
`;

const Marks = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: #7200e0;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1.4;
`;
